/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { find } from "lodash"
import { Fragment, useEffect, useMemo, useState } from "react"
import { useParams, useHistory, useLocation } from "react-router-dom"
import { useDispatch, useSelector } from "react-redux"
import { actionCreator } from "store/store"
import { CATALOGUES, MENUS } from "constants/routes"
import { useTranslationsMap } from "common/utils/i18n"
import {
  WithMenuHeader,
  WithMenuHeaderInjectedProps,
} from "components/hoc/WithMenuHeader"
import { storageHelper } from "utils/storage"
import { getResponseErrorEventLabels, TroyWebTracker } from "utils/tracking"
import {
  EventAction,
  EventActionEng,
  EventCategoryEng,
} from "constants/tracking"
import {
  isMartDepartmentStructure,
  getIsSellingTimeStructure,
  isMartDepartmentV2Structure,
  getIsSellingTimeStructureV2,
  getBusinessTypeGrabXFacetVal,
} from "utils/menu_store_helper"
import { getItemList, getItemWithoutPhotoCount } from "utils/menu_helper"
import { AppState } from "store/store.type"
import { ResponseWithError } from "types/api"
import { MenuEditorContent } from "components/MenuEditorContent"
import { MenuDrawerContainer } from "containers/MenuDrawerContainer"
import useInventoryContext from "hooks/useInventoryContext"
import useSingleStoreUser from "hooks/useSingleStoreUser"
import {
  BusinessType,
  Menu,
  MenuGroup,
  MerchantType,
  Section,
} from "types/models/menu"
import { getIsMenuGroupMartDepartmentStructure } from "utils/menu_group"
import {
  enableBulkUploadProcessingBannerByUploadTypeDefaultValue,
  MenuEditorTabKey,
  menuTabEventCategories,
} from "constants/menu"
import {
  getGrabXValue,
  FacetTypes,
  GrabXDataTypes,
  MerchantPortalFeatureFlag,
  withObserverModeFacet,
} from "grabx"
import { getUserCountry } from "troy-common/utils"
import { getUserCityID } from "utils/authentication"
import { actions as menuDuckActions } from "store/duck/menu"
import useRtkQueryMigrationEndpointsFlag from "hooks/useRtkQueryMigrationEndpointsFlag"
import { useLazyFetchMerchantDetailQuery } from "queries/menuApi"
import { StockManagementAutoScrollProvider } from "./StockManagementAutoScrollContext"
import { useShownTabs } from "./hooks/useShownTab"

interface MenuEditorProps {
  routePrefix?: string
  eventLabels?: { [key: string]: string }
}

export const MenuEditor = ({
  routePrefix: propRoutePrefix,
  eventLabels,
}: MenuEditorProps) => {
  const { menuParentID, tabKey } = useParams<{
    menuParentID: string
    tabKey: string
  }>()
  const location = useLocation()

  const dispatch = useDispatch()
  const [hasInitialised, setHasInitialised] = useState(false)

  const {
    flags: { fetchMerchantDetail: isFetchMerchantDetailRtkEnabled },
    isLoading: isLoadingRtkQueryMigrationEndpointsFlag,
  } = useRtkQueryMigrationEndpointsFlag()
  const [triggerFetchMerchantDetailRtk] = useLazyFetchMerchantDetailQuery()

  const history = useHistory()

  const {
    isMartStore,
    isMenuGroup,
    merchantGroupID,
    isSellingTimeStructure,
    items,
  } = useSelector((state: AppState) => state.menu)

  const { isInventory } = useInventoryContext()

  const menuGroupID = isMenuGroup && !isInventory ? menuParentID : undefined
  const merchantID = isMenuGroup && !isInventory ? undefined : menuParentID

  const routePrefix = useMemo(
    () => propRoutePrefix ?? (isMartStore ? CATALOGUES : MENUS),
    [propRoutePrefix, isMartStore],
  )

  const countryCode = storageHelper.getUserCountry() || ""

  const shownTabs = useShownTabs()

  const activeTabKey = shownTabs.includes(tabKey as MenuEditorTabKey)
    ? tabKey
    : shownTabs[0]

  const fetchMerchantDetail = async () => {
    if (!merchantID) return undefined
    if (isFetchMerchantDetailRtkEnabled) {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.rtkMigration,
        action: EventActionEng.fetchMerchantDetail,
        labels: {
          MERCHANT_ID: merchantID,
          IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
          FROM_RTK_QUERY: true,
          FROM_OLD_REDUX: false,
          ACTION_NAME: "fetchMerchantDetail",
        },
      })
      const result = await triggerFetchMerchantDetailRtk({
        merchantID,
        countryCode,
      })
      if (result.error) {
        return {
          type: `${menuDuckActions.FETCH_MERCHANT_DETAIL}_FAIL`,
          error: result.error,
        }
      }
      // Sync Redux state so downstream consumers (isMartStore, outletV2, etc.) stay up-to-date
      dispatch({
        type: `${menuDuckActions.FETCH_MERCHANT_DETAIL}_SUCCESS`,
        payload: { data: result.data },
      })
      return {
        type: `${menuDuckActions.FETCH_MERCHANT_DETAIL}_SUCCESS`,
        payload: { data: result.data },
      }
    }

    TroyWebTracker.sendEvent({
      category: EventCategoryEng.rtkMigration,
      action: EventActionEng.fetchMerchantDetail,
      labels: {
        MERCHANT_ID: merchantID,
        IS_RTK_ENABLED: isFetchMerchantDetailRtkEnabled,
        FROM_RTK_QUERY: false,
        FROM_OLD_REDUX: true,
        ACTION_NAME: "fetchMerchantDetail",
      },
    })
    return dispatch(
      actionCreator.menu.fetchMerchantDetail({
        merchantID,
        countryCode,
      }),
    )
  }

  const fetchMenuGroupDetail = () =>
    menuGroupID &&
    dispatch(
      actionCreator.menu.fetchMenuGroupDetail({
        menuGroupID,
        merchantGroupID,
      }),
    )

  const textMap = useTranslationsMap({
    errorExceedMaxCategoryLimit:
      "catalogue.errors.category.exceedMaxCategoryLimit",
  })

  const getIsMenuParentSellingTimeStructure = (merchantType: MerchantType) => {
    const store = merchantType
    if ("merchantID" in store) {
      return getIsSellingTimeStructure(store)
    }
    return getIsSellingTimeStructureV2(store)
  }

  const getIsMartDepartmentStructure = (merchantType: MerchantType) => {
    const store = merchantType
    if ("merchantID" in store) {
      return isMartDepartmentStructure(store)
    }
    return isMartDepartmentV2Structure(store)
  }

  const fetchCatalogueErrorCallback = (response: ResponseWithError) => {
    const { message = "", reason = "" } = response ?? {}

    if (message === "too many item classes" && reason === "rate_exceeded") {
      return textMap.errorExceedMaxCategoryLimit
    }

    return true
  }

  const fetchCatalogue = async (isMenuParentSellingTimeStructure: boolean) => {
    const [resp] = await Promise.all([
      dispatch(
        actionCreator.menu.fetchCatalogue({
          merchantID,
          menuGroupID,
          merchantGroupID,
          showGlobalLoading: true,
          errorCallback: fetchCatalogueErrorCallback,
          isSellingTimeStructure: isMenuParentSellingTimeStructure,
        }),
      ),
      dispatch(
        actionCreator.menu.fetchDepartments({
          merchantID,
          menuGroupID,
          merchantGroupID,
        }),
      ),
    ])
    return resp
  }

  const fetchMenuFeatureFlag = async () => {
    const facets = {
      [FacetTypes.Merchant]: merchantID,
      [FacetTypes.MexMerchant]: merchantGroupID,
      [FacetTypes.City]: getUserCityID(),
      [FacetTypes.Country]: getUserCountry(),
    }

    const enableAlcoholSetting = await getGrabXValue(
      isMenuGroup
        ? MerchantPortalFeatureFlag.enableAlcoholSettingMenuGroupV2
        : MerchantPortalFeatureFlag.enableAlcoholSetting,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )
    const enableTobaccoSelling = await getGrabXValue(
      isMenuGroup
        ? MerchantPortalFeatureFlag.TobaccoSaleEnabledForMenuGroupV2
        : MerchantPortalFeatureFlag.TobaccoSaleEnabled,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )
    const enableBulkUploadProcessingBannerByUploadType = await getGrabXValue(
      MerchantPortalFeatureFlag.portalEnableBulkUploadProcessingBannerByUploadType,
      GrabXDataTypes.Json,
      enableBulkUploadProcessingBannerByUploadTypeDefaultValue,
      facets,
    )
    const enablePartialMenu = await getGrabXValue(
      MerchantPortalFeatureFlag.portalEnablePartialMenu,
      GrabXDataTypes.Boolean,
      false,
      withObserverModeFacet(facets),
    )

    dispatch(actionCreator.menu.setAlcoholSetting(enableAlcoholSetting))
    dispatch(actionCreator.menu.setTobaccoSetting(enableTobaccoSelling))
    dispatch(
      actionCreator.menu.setEnableBulkUploadProcessingBannerByUploadType(
        enableBulkUploadProcessingBannerByUploadType,
      ),
    )

    return { enablePartialMenu }
  }

  const fetchBusinessTypeFeatureFlag = async (businessType: string) => {
    const facets = {
      [FacetTypes.Merchant]: merchantID,
      [FacetTypes.City]: getUserCityID(),
      [FacetTypes.Country]: getUserCountry(),
      [FacetTypes.BusinessType]: businessType,
    }

    const enableMenuTranslationPortalCall = await getGrabXValue(
      MerchantPortalFeatureFlag.enableMenuTranslationPortal,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )
    const enableGrabMexProImageEditor = await getGrabXValue(
      MerchantPortalFeatureFlag.portalEnableGrabMexProImageEditor,
      GrabXDataTypes.Boolean,
      false,
      withObserverModeFacet(facets),
    )

    dispatch(
      actionCreator.menu.setEnableMenuTranslationPortal(
        enableMenuTranslationPortalCall,
      ),
    )
    dispatch(
      actionCreator.menu.setEnableGrabMexProImageEditor(
        enableGrabMexProImageEditor,
      ),
    )
  }

  const fetchBusinessTypeMenuGroupFeatureFlag = async (
    businessType: string,
  ) => {
    const facets = {
      [FacetTypes.MexMerchant]: merchantGroupID,
      [FacetTypes.Country]: getUserCountry(),
      [FacetTypes.BusinessType]: businessType,
    }

    const enableMenuGroupMenuTranslationPortalCall = await getGrabXValue(
      MerchantPortalFeatureFlag.enableMenuGroupMenuTranslationPortal,
      GrabXDataTypes.Boolean,
      false,
      facets,
    )

    dispatch(
      actionCreator.menu.setEnableMenuTranslationPortal(
        enableMenuGroupMenuTranslationPortalCall,
      ),
    )
    // EnhanceImage API is not available for menu group yet
    dispatch(actionCreator.menu.setEnableGrabMexProImageEditor(false))
  }

  const fetchMenuNonDepartment = async (
    isMenuParentSellingTimeStructure: boolean,
  ) => {
    const lastSegment = location.pathname.split("/").pop()

    const resp: any = await dispatch(
      actionCreator.menu.fetchMenu({
        merchantID,
        menuGroupID,
        merchantGroupID,
        showGlobalLoading: true,
        isSellingTimeStructure: isMenuParentSellingTimeStructure,
      }),
    )

    const menu: Menu = resp?.payload?.data
    if (menu) {
      const itemList = getItemList(menu)
      const selectedSectionID = itemList.find(
        item => item.itemID === lastSegment,
      )?.sectionID

      if (!isMenuParentSellingTimeStructure && menu.sections) {
        if (selectedSectionID) {
          const section: Section =
            find(
              menu.sections,
              ({ sectionID }) => sectionID === selectedSectionID,
            ) ?? menu.sections[0]
          await dispatch(actionCreator.menu.selectSection(section))
        }
      }
    }
    return resp
  }

  const fetchMenu = async () => {
    const t0 = performance.now()
    dispatch(actionCreator.menu.clearMenu())
    const { enablePartialMenu } = await fetchMenuFeatureFlag()
    let resp: any
    if (menuGroupID) resp = await fetchMenuGroupDetail()
    else resp = await fetchMerchantDetail()

    if (!resp?.type?.endsWith("_FAIL")) {
      const menuGroup: MenuGroup | undefined = resp?.payload?.data?.menuGroup
      const merchant: MerchantType | undefined = resp?.payload?.data?.merchant

      if (menuGroup) {
        await fetchBusinessTypeMenuGroupFeatureFlag(
          getBusinessTypeGrabXFacetVal(
            menuGroup.businessType || BusinessType.Food,
          ), // endpoint return empty when the businessType is 0
        )
        const isMartDept = getIsMenuGroupMartDepartmentStructure(menuGroup)
        resp = isMartDept
          ? await fetchCatalogue(isSellingTimeStructure)
          : await fetchMenuNonDepartment(isSellingTimeStructure)
      } else if (merchant) {
        await fetchBusinessTypeFeatureFlag(
          getBusinessTypeGrabXFacetVal(
            merchant.businessType || BusinessType.Food,
          ), // endpoint return empty when the businessType is 0
        )
        const merchantIsSellingTimeStructure = getIsMenuParentSellingTimeStructure(
          merchant,
        )
        const isMartDept = getIsMartDepartmentStructure(merchant)
        // Skip fetching full menu/catalogue if using partial menu API
        if (isMartDept && enablePartialMenu) {
          dispatch(actionCreator.menu.setEnablePartialMenu(true))
          // Partial menu API handles fetching, so we skip fetchCatalogue
          resp = { type: "SKIP_FETCH" }
        } else {
          resp = isMartDept
            ? await fetchCatalogue(merchantIsSellingTimeStructure)
            : await fetchMenuNonDepartment(merchantIsSellingTimeStructure)
        }
      }
    } else {
      TroyWebTracker.sendEvent({
        category: EventCategoryEng.errorMerchantV2,
        action: EventActionEng.errorFetchMerchantV2,
        labels: {
          merchantID: resp?.meta?.previousAction?.payload?.merchantID,
          errorMessage: resp?.error,
          username: storageHelper.getEmail(),
        },
      })
    }

    const t1 = performance.now()

    const category = menuTabEventCategories[activeTabKey]

    let labels = {
      ...(merchantID
        ? { STORE_ID: merchantID }
        : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
      LOAD_TIME: t1 - t0,
      LOADING_STATUS: "SUCCESS",
      REASON: "",
      ERROR_MSG: "",
      NUM_ITEMS_PHOTO_MISSING: getItemWithoutPhotoCount(items),
      ...(resp?.type === "SKIP_FETCH" ? { IS_PARTIAL_MENU: true } : {}),
      ...eventLabels,
    }

    if (resp?.type?.endsWith("_FAIL")) {
      labels = {
        ...labels,
        LOADING_STATUS: "FAILURE",
        ...getResponseErrorEventLabels(resp),
      }
    }

    TroyWebTracker.sendEvent({
      category,
      action: EventAction.pageView,
      labels,
    })
    setHasInitialised(true)
  }

  useEffect(() => {
    if (
      tabKey &&
      !!shownTabs.length &&
      !shownTabs.includes(tabKey as MenuEditorTabKey)
    ) {
      // replace url of invalid tabkey
      history.replace(`${routePrefix}/${menuParentID}`)
    }
  }, [shownTabs, routePrefix, menuParentID, tabKey, history])

  useEffect(() => {
    if (shownTabs.length && !isLoadingRtkQueryMigrationEndpointsFlag) {
      fetchMenu()
    }
    // isFetchMerchantDetailRtkEnabled resolves in the same render as
    // isLoadingRtkQueryMigrationEndpointsFlag (same hook), so the correct
    // flag value is always captured when this effect fires.
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [
    shownTabs,
    isLoadingRtkQueryMigrationEndpointsFlag,
    isFetchMerchantDetailRtkEnabled,
  ])

  return (
    <Fragment>
      <StockManagementAutoScrollProvider>
        <MenuEditorContent
          data-testid="menu-editor-content"
          shownTabs={shownTabs}
          routePrefix={routePrefix}
          hideContent={!hasInitialised || !shownTabs.length}
        />
        <MenuDrawerContainer />
      </StockManagementAutoScrollProvider>
    </Fragment>
  )
}

export const MenuEditorWithHeaderWrapped = ({
  setHeaderTitle,
  setBackLink,
}: WithMenuHeaderInjectedProps) => {
  const { isMartStore, outletV2, isMenuGroup } = useSelector(
    (state: AppState) => state.menu,
  )
  const merchantName = outletV2?.name
  const location = useLocation()
  const history = useHistory()
  const dispatch = useDispatch()

  const handleFixPathname = () => {
    let newPathname = location.pathname
    newPathname = isMartStore
      ? location.pathname.replace(MENUS, CATALOGUES)
      : location.pathname.replace(CATALOGUES, MENUS)
    if (location.pathname !== newPathname) history.replace(newPathname)
  }

  const textMap = useTranslationsMap({
    titleMenu: "menu.title",
    titleCatalogue: "catalogue.title",
  })

  const defaultTitle = isMartStore ? textMap.titleCatalogue : textMap.titleMenu

  const { isSingleStoreUser } = useSingleStoreUser()
  const updateHeader = () => {
    // Show the name of the store in detail page
    setHeaderTitle(merchantName ?? defaultTitle)

    /**
     * check if the `isSingleStoreUser` value is explicitly set to `false`
     * to ensure function only runs after the data has been loaded
     */
    if (isSingleStoreUser === false || isMenuGroup) {
      const backLink = isMartStore ? CATALOGUES : MENUS

      setBackLink(backLink)
    }
  }

  useEffect(updateHeader, [merchantName, isSingleStoreUser])

  useEffect(handleFixPathname, [isMartStore, location.pathname])

  // unmount
  useEffect(
    () => () => {
      dispatch(actionCreator.menu.clearStore())
    },
    [],
  )
  return <MenuEditor />
}

export const MenuEditorWithHeader = WithMenuHeader()(
  MenuEditorWithHeaderWrapped,
)
